import { firebaseConfig, isIOSDevice, isStandalone, pushPermission } from './pushNotifications';

// Which card the chat shows to get the member's notifications on (nutivate-web#49). The rules are the approved
// "Who sees which card" table, checked top to bottom; the first match wins. whichInvite is kept free of the
// browser so every row can be tested; currentInvite, at the end, fills it in from this browser.

// One key for every card, so "Not now" on any of them hides them all for a week.
export const DISMISSED_KEY = 'pushInviteDismissedAt';
export const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

// Apple allows web push from iOS 16.4.
const IOS_PUSH_FROM = [16, 4];

// Browsers inside other apps, which can't add Nutivate to the Home Screen or get push.
const IOS_IN_APP = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|MessengerForiOS|musical_ly|BytedanceWebview|TikTok|\bGSA\/|LinkedInApp|Snapchat|Twitter|Line\//;
// "; wv)" marks an Android WebView.
const ANDROID_IN_APP = /Instagram|FBAV|FB_IAB|musical_ly|TikTok|\bGSA\/|; wv\)/;

export const deviceOf = (device) => {
  if (isIOSDevice(device)) return 'ios';
  return /Android/i.test(device.ua) ? 'android' : 'computer';
};

// [major, minor] from "iPhone OS 16_3", or from "Version/16.3" on iPads that look like a Mac; null when unknown.
export const iosVersion = (ua) => {
  const match = ua.match(/(?:iPhone|CPU) OS (\d+)_(\d+)/) || ua.match(/Version\/(\d+)\.(\d+)/);
  return match ? [Number(match[1]), Number(match[2])] : null;
};

const tooOldForPush = (ua) => {
  const version = iosVersion(ua);
  if (!version) return false;
  return version[0] < IOS_PUSH_FROM[0] || (version[0] === IOS_PUSH_FROM[0] && version[1] < IOS_PUSH_FROM[1]);
};

// Which install steps an iPhone browser needs: each hides Share in a different place.
export const iosBrowser = (ua) => {
  if (/CriOS/.test(ua)) return 'chrome';
  if (/FxiOS|EdgiOS|OPiOS|OPT\/|Ddg\/|YaBrowser/.test(ua) || !/Safari\//.test(ua)) return 'other';
  return 'safari';
};

// One of 'blocked', 'turnOn', 'install', 'openInSafari', 'openInChrome', or null for no card.
//   ua, platform, maxTouchPoints: from navigator
//   standalone: opened from the Home Screen / as the installed app
//   permission: Notification.permission, or 'unsupported' when the browser has no web push
//   pushConfigured: this build has Firebase settings
//   dismissedAt: when "Not now" was last tapped (ms), or 0
//   installEventAvailable: Chrome has offered to install (beforeinstallprompt)
export const whichInvite = ({
  ua,
  platform,
  maxTouchPoints,
  standalone,
  permission,
  pushConfigured,
  dismissedAt,
  installEventAvailable,
  now,
}) => {
  if (!pushConfigured) return null;
  if (permission === 'granted') return null;
  // A time ahead of now means the clock was wrong when "Not now" was tapped; it counts as over.
  const sinceDismissed = now - dismissedAt;
  if (dismissedAt && sinceDismissed >= 0 && sinceDismissed < DISMISS_MS) return null;

  const device = deviceOf({ ua, platform, maxTouchPoints });
  if (device === 'android') {
    // No Google push service on Huawei's own browser.
    if (/HuaweiBrowser/.test(ua)) return null;
    // In-app browsers usually have no web push, and their Settings steps differ, so they're sent to Chrome whatever
    // the permission says.
    if (ANDROID_IN_APP.test(ua)) return 'openInChrome';
  }

  // The phone won't ask again, so the card explains Settings instead.
  if (permission === 'denied') return 'blocked';

  if (device === 'ios') {
    if (tooOldForPush(ua)) return null;
    if (standalone) return permission === 'default' ? 'turnOn' : null;
    // Safari tabs have no web push at all, so these don't depend on permission.
    if (IOS_IN_APP.test(ua)) return 'openInSafari';
    return 'install';
  }

  if (device === 'android') {
    if (permission !== 'default') return null;
    return installEventAvailable && !standalone ? 'install' : 'turnOn';
  }

  // Computers: no install offer.
  return permission === 'default' ? 'turnOn' : null;
};

const dismissedAt = () => {
  try {
    return Number(localStorage.getItem(DISMISSED_KEY) || 0);
  } catch (error) {
    return 0;
  }
};

export const currentDevice = () => ({
  ua: navigator.userAgent,
  platform: navigator.platform,
  maxTouchPoints: navigator.maxTouchPoints,
});

// The card for this browser right now.
export const currentInvite = ({ installEventAvailable }) =>
  whichInvite({
    ...currentDevice(),
    standalone: isStandalone(),
    permission: pushPermission(),
    pushConfigured: !!firebaseConfig,
    dismissedAt: dismissedAt(),
    installEventAvailable,
    now: Date.now(),
  });
